// src/components/cards/CardBacksGallery.jsx
// Sélecteur de dos de carte cosmétiques (un seul dos actif par joueur).
// États : possédé (sélectionnable), verrouillé (conditions affichées), achetable.
import React, { useEffect, useState, useCallback } from 'react';
import { getCardBacks, selectCardBack, buyCardBack, cardBackUrl } from '../../api/cardBackService';
import './CardBacksGallery.css';

const CardBacksGallery = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // backId en cours d'action
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getCardBacks();
      setState(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSelect = async (backId) => {
    setBusy(backId);
    setMessage(null);
    try {
      const res = await selectCardBack(backId);
      if (res.success) {
        setState((s) => ({ ...s, selected: backId }));
        setMessage('Dos de carte appliqué à votre deck.');
      }
    } catch (e) {
      setMessage(e.response?.data?.message || 'Sélection impossible');
    } finally { setBusy(null); }
  };

  const handleBuy = async (backId) => {
    setBusy(backId);
    setMessage(null);
    try {
      const res = await buyCardBack(backId);
      if (res.success) {
        setMessage('Dos acheté ! Vous pouvez maintenant le sélectionner.');
        await load();
      }
    } catch (e) {
      setMessage(e.response?.data?.message === 'not_enough_coins' ? 'Or insuffisant' : 'Achat impossible');
    } finally { setBusy(null); }
  };

  if (loading) return <div className="cb-loading">Chargement des dos de carte…</div>;
  if (!state || !state.backs) return null;

  return (
    <div className="cardbacks-section">
      <p className="cb-rule">
        Un seul dos de carte actif à la fois — appliqué à l'ensemble de votre deck.
        Le dos neutre ne révèle jamais le contenu de votre deck.
      </p>
      {message && <div className="cb-message">{message}</div>}
      <div className="cb-grid">
        {state.backs.map((back) => {
          const isActive = state.selected === back.id;
          return (
            <div
              key={back.id}
              className={`cb-item ${back.owned ? 'owned' : 'locked'} ${isActive ? 'active' : ''}`}
            >
              <div className="cb-image-wrap">
                <img
                  src={cardBackUrl(back.id)}
                  alt={back.name}
                  onError={(e) => { e.target.onerror = null; e.target.src = cardBackUrl('neutral'); }}
                />
                {!back.owned && <div className="cb-lock">🔒</div>}
                {isActive && <div className="cb-active-badge">Actif</div>}
              </div>
              <div className="cb-info">
                <h4>{back.name}</h4>
                <p className="cb-desc">{back.description}</p>
                {back.owned ? (
                  <button
                    className="cb-select-btn"
                    disabled={isActive || busy === back.id}
                    onClick={() => handleSelect(back.id)}
                  >
                    {isActive ? 'Dos actif' : 'Utiliser ce dos'}
                  </button>
                ) : (
                  <ul className="cb-sources">
                    {back.sources.filter((s) => s.type !== 'shop').map((s, i) => (
                      <li key={i}>✦ {s.label}</li>
                    ))}
                    {back.shopPrice != null && (
                      <li className="cb-buy-row">
                        <button
                          className="cb-buy-btn"
                          disabled={busy === back.id}
                          onClick={() => handleBuy(back.id)}
                        >
                          Acheter — {back.shopPrice} or
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardBacksGallery;
